import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '../../../api/apiClient';
import extractApiError from '../../../api/extractApiError';

interface User {
  id: number;
  name: string | null;
  mobile: string;
  has_parent: boolean;
}

interface AgentState {
  loading: boolean;
  searchResults: User[];
  selectedUser: User | null;
  error: string | null;
  currentStep: 'search' | 'confirm' | 'sendOTP' | 'verifyOTP' | 'selectPublisher';
  selectedPublisherId: number | null;
  existingAgents: User[];
}

const initialState: AgentState = {
  loading: false,
  searchResults: [],
  selectedUser: null,
  error: null,
  currentStep: 'search',
  selectedPublisherId: null,
  existingAgents: [],
};

interface SearchAgentPayload {
  mobile: string;
}

interface SearchAgentResponse {
  message: string;
  users: User[];
}

interface SendOTPPayload {
  child_user_id: number;
}

interface VerifyOTPPayload {
  child_user_id: number;
  code: string;
}

interface GrantPublisherPayload {
  child_user_id: number;
  publisher_id: number;
}

interface RevokePublisherPayload {
  child_user_id: number;
  publisher_id: number;
}

interface GetPublisherAgentsPayload {
  publisher_id: number;
}

interface RemoveAgentPayload {
  child_user_id: number;
}

interface GetAgentPublishersPayload {
  child_user_id: number;
}

interface Publisher {
  id: number;
  name: string;
}

// Get existing agents
export const getAgents = createAsyncThunk<User[], void>(
  'agent/getAgents',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/v2/publisher/agents/');

      // Handle different possible response structures
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data.children && Array.isArray(response.data.children)) {
        return response.data.children;
      } else if (response.data.agents && Array.isArray(response.data.agents)) {
        return response.data.agents;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }

      return [];
    } catch (error: any) {
      return rejectWithValue(extractApiError(error, 'Failed to get agents'));
    }
  }
);

// Search for agents by mobile number
export const searchAgent = createAsyncThunk<SearchAgentResponse, SearchAgentPayload>(
  'agent/search',
  async ({ mobile }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/v2/publisher/agents/search', { mobile });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error, 'Failed to search agent'));
    }
  }
);

// Send OTP to selected agent
export const sendOTP = createAsyncThunk<any, SendOTPPayload>(
  'agent/sendOTP',
  async ({ child_user_id }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/v2/publisher/agents/sendOTP', { child_user_id });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error, 'Failed to send OTP'));
    }
  }
);

// Verify OTP code
export const verifyOTP = createAsyncThunk<any, VerifyOTPPayload>(
  'agent/verifyOTP',
  async ({ child_user_id, code }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/v2/publisher/agents/verifyOTP', {
        child_user_id,
        code,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error, 'Failed to verify OTP'));
    }
  }
);

// Grant publisher to agent
export const grantPublisher = createAsyncThunk<any, GrantPublisherPayload>(
  'agent/grantPublisher',
  async ({ child_user_id, publisher_id }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/v2/publisher/agents/grantPublisher', {
        child_user_id,
        publisher_id,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error, 'Failed to grant publisher'));
    }
  }
);

// Revoke publisher access from agent
export const revokePublisher = createAsyncThunk<any, RevokePublisherPayload>(
  'agent/revokePublisher',
  async ({ child_user_id, publisher_id }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/v2/publisher/agents/revokePublisher', {
        child_user_id,
        publisher_id,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error, 'Failed to revoke publisher'));
    }
  }
);

// Get publishers that agent has access to
export const getAgentPublishers = createAsyncThunk<Publisher[], GetAgentPublishersPayload>(
  'agent/getAgentPublishers',
  async ({ child_user_id }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/v2/publisher/agents/publishers', {
        child_user_id,
      });
      return response.data.publishers || [];
    } catch (error: any) {
      return rejectWithValue(extractApiError(error, 'Failed to get agent publishers'));
    }
  }
);

// Remove agent completely (revokes all access and removes the agent)
export const removeAgent = createAsyncThunk<any, RemoveAgentPayload>(
  'agent/removeAgent',
  async ({ child_user_id }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/v2/publisher/agents/remove', {
        child_user_id,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error, 'Failed to remove agent'));
    }
  }
);

const agentSlice = createSlice({
  name: 'agent',
  initialState,
  reducers: {
    setSelectedUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload;
    },
    setCurrentStep: (state, action: PayloadAction<'search' | 'confirm' | 'sendOTP' | 'verifyOTP' | 'selectPublisher'>) => {
      state.currentStep = action.payload;
    },
    setSelectedPublisherId: (state, action: PayloadAction<number | null>) => {
      state.selectedPublisherId = action.payload;
    },
    resetAgentState: (state) => {
      state.loading = false;
      state.searchResults = [];
      state.selectedUser = null;
      state.error = null;
      state.currentStep = 'search';
      state.selectedPublisherId = null;
      state.existingAgents = [];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get existing agents
      .addCase(getAgents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAgents.fulfilled, (state, action) => {
        state.loading = false;
        state.existingAgents = action.payload;
      })
      .addCase(getAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Search agent
      .addCase(searchAgent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchAgent.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload.users;
      })
      .addCase(searchAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Send OTP
      .addCase(sendOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendOTP.fulfilled, (state) => {
        state.loading = false;
        state.currentStep = 'verifyOTP';
      })
      .addCase(sendOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Verify OTP
      .addCase(verifyOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state) => {
        state.loading = false;
        state.currentStep = 'selectPublisher';
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Grant Publisher
      .addCase(grantPublisher.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(grantPublisher.fulfilled, (state) => {
        state.loading = false;
        // Success - will show toast and close modal
      })
      .addCase(grantPublisher.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Revoke Publisher
      .addCase(revokePublisher.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(revokePublisher.fulfilled, (state) => {
        state.loading = false;
        // Success - will show toast
      })
      .addCase(revokePublisher.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Get Agent Publishers
      .addCase(getAgentPublishers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAgentPublishers.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(getAgentPublishers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Remove Agent
      .addCase(removeAgent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeAgent.fulfilled, (state) => {
        state.loading = false;
        // Success - will show toast
      })
      .addCase(removeAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSelectedUser, setCurrentStep, setSelectedPublisherId, resetAgentState, clearError } = agentSlice.actions;
export default agentSlice.reducer;
